import type { Infer } from 'spacetimedb';
import {
  reducers,
  MySkillsRow,
  MySkillBarRow,
  type ErrorContext
} from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { ReducerClient } from '../core/ReducerClient';
import type { Logger } from '../types';

function parseJsonArray(json: string | undefined | null): string[] {
  try { return JSON.parse(json ?? '[]') as string[]; } catch { return []; }
}

type MySkillsTableRow = Infer<typeof MySkillsRow>;
type MySkillBarTableRow = Infer<typeof MySkillBarRow>;

export interface SkillData {
  readonly skillId: string;
  readonly displayName: string;
  readonly description: string;
  readonly tags: string[];
  readonly cooldownTicks: number;
  readonly staminaCost: number;
  readonly durationTicks: number;
  readonly range: number;
  readonly damage: number;
  readonly radius: number;
  readonly emoji: string;
  readonly inputsJson: string;
  readonly metaChildren: string[];
  readonly lastUsedTick: number;
}

export interface SkillBarData {
  readonly playerId: string;
  readonly skills: string[]; // skill_ids
}

export interface ActiveZoneData {
  readonly zoneId: string;
  readonly zoneType: string;
  readonly skillId: string;
  readonly sourceEntityId: string;
  readonly instanceId: string;
  readonly mapId: string;
  readonly centerX: number;
  readonly centerY: number;
  readonly radius: number;
  readonly expiresAtTick: number;
}

export class SkillModule {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly reducerClient: ReducerClient,
    private readonly logger: Logger = () => undefined
  ) { }

  public async subscribeToSkills(
    onUpdate: (skills: SkillData[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const skillsView = connection.db.mySkills;

    if (!skillsView) {
      this.logger('my_skills view not available yet - bindings need regeneration');
      return { unsubscribe: () => { } };
    }

    const emitRows = (): void => {
      const rows = Array.from(skillsView.iter()).map((row: MySkillsTableRow) => ({
        tags: Array.isArray((row as MySkillsTableRow & { tags?: string[] | null }).tags)
          ? ((row as MySkillsTableRow & { tags?: string[] | null }).tags ?? [])
            .filter((tag): tag is string => typeof tag === 'string')
          : [],
        skillId: row.skillId,
        displayName: row.displayName,
        description: row.description,
        cooldownTicks: Number(row.cooldownTicks ?? 0),
        staminaCost: Number((row as MySkillsTableRow & { staminaCost?: number | null }).staminaCost ?? 0),
        durationTicks: Number((row as MySkillsTableRow & { durationTicks?: number | null }).durationTicks ?? 0),
        range: Number(row.range ?? 0),
        damage: Number(row.damage ?? 0),
        radius: Number(row.radius ?? 0),
        emoji: row.emoji,
        inputsJson: row.inputsJson,
        metaChildren: parseJsonArray((row as MySkillsTableRow & { metaChildrenJson?: string }).metaChildrenJson),
        lastUsedTick: Number(row.lastUsedTick ?? 0),
      }));
      onUpdate(rows);
    };

    const handleChange = (): void => { emitRows(); };

    skillsView.onInsert(handleChange);
    skillsView.onDelete(handleChange);
    skillsView.onUpdate(handleChange);

    emitRows();

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM my_skills';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        skillsView.removeOnInsert(handleChange);
        skillsView.removeOnDelete(handleChange);
        skillsView.removeOnUpdate(handleChange);
      };

      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Skills subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }

  public async subscribeToSkillBar(
    onUpdate: (bar: SkillBarData | null) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const barView = connection.db.mySkillBar;

    if (!barView) {
      this.logger('my_skill_bar view not available yet - bindings need regeneration');
      return { unsubscribe: () => { } };
    }

    const emitRows = (): void => {
      const rows = Array.from(barView.iter());
      if (rows.length === 0) {
        onUpdate(null);
        return;
      }
      const row = rows[0] as MySkillBarTableRow;
      let skills: string[] = [];
      try {
        skills = JSON.parse(row.skillsJson ?? '[]') as string[];
      } catch { /* empty */ }
      onUpdate({
        playerId: row.playerId,
        skills,
      });
    };

    const handleChange = (): void => { emitRows(); };

    barView.onInsert(handleChange);
    barView.onDelete(handleChange);
    barView.onUpdate(handleChange);

    emitRows();

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM my_skill_bar';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        barView.removeOnInsert(handleChange);
        barView.removeOnDelete(handleChange);
        barView.removeOnUpdate(handleChange);
      };

      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Skill bar subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }

  public async subscribeToActiveZones(
    onUpdate: (zones: ActiveZoneData[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const table = ((connection.db as any).myVisibleActiveZones ?? (connection.db as any).my_visible_active_zones) as
      | {
        iter: () => Iterable<any>;
        onInsert: (cb: () => void) => void;
        onDelete: (cb: () => void) => void;
        onUpdate: (cb: () => void) => void;
        removeOnInsert: (cb: () => void) => void;
        removeOnDelete: (cb: () => void) => void;
        removeOnUpdate: (cb: () => void) => void;
      }
      | undefined;

    if (!table) {
      this.logger('my_visible_active_zones view not available yet - bindings may need regeneration');
      return { unsubscribe: () => { } };
    }

    const mapRow = (row: any): ActiveZoneData => ({
      zoneId: String(row.zoneId ?? row.zone_id ?? ''),
      zoneType: String(row.zoneType ?? row.zone_type ?? ''),
      skillId: String(row.skillId ?? row.skill_id ?? ''),
      sourceEntityId: String(row.sourceEntityId ?? row.source_entity_id ?? ''),
      instanceId: String(row.instanceId ?? row.instance_id ?? ''),
      mapId: String(row.mapId ?? row.map_id ?? ''),
      centerX: Number(row.centerX ?? row.center_x ?? 0),
      centerY: Number(row.centerY ?? row.center_y ?? 0),
      radius: Number(row.radius ?? 0),
      expiresAtTick: Number(row.expiresAtTick ?? row.expires_at_tick ?? 0),
    });

    const emitRows = (): void => {
      const rows = Array.from(table.iter()).map(mapRow);
      onUpdate(rows);
    };
    const handleChange = (): void => emitRows();
    table.onInsert(handleChange);
    table.onDelete(handleChange);
    table.onUpdate(handleChange);

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM my_visible_active_zones';
    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;
      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        table.removeOnInsert(handleChange);
        table.removeOnDelete(handleChange);
        table.removeOnUpdate(handleChange);
      };
      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Active zones subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });
      handle = builder.subscribe(sql);
    });
  }

  public async addToSkillBar(skillId: string): Promise<void> {
    return this.reducerClient.callFireAndForget(
      'add_to_skill_bar',
      reducers.addToSkillBar.paramsType,
      { skillId },
    );
  }

  public async addToSkillBarAt(skillId: string, slotIndex: number): Promise<void> {
    return this.reducerClient.callFireAndForget(
      'add_to_skill_bar_at',
      reducers.addToSkillBarAt.paramsType,
      { skillId, slotIndex },
    );
  }

  public async removeFromSkillBar(skillId: string): Promise<void> {
    return this.reducerClient.callFireAndForget(
      'remove_from_skill_bar',
      reducers.removeFromSkillBar.paramsType,
      { skillId },
    );
  }

  public async cancelStatusEffect(effectId: string): Promise<void> {
    return this.reducerClient.callFireAndForget(
      'cancel_status_effect',
      reducers.cancelStatusEffect.paramsType,
      { effectId },
    );
  }
}
